import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SearchBar } from '../components/SearchBar';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FilmCard } from '../components/FilmCard';
import { useFilms } from '../hooks/useFilms';
import { ArrowRight, Star, Film, Loader2 } from 'lucide-react';
export function Home() {
  const {
    isLoggedIn
  } = useAuth();
  const {
    films,
    loading
  } = useFilms();
  const recommendations = films.slice(0, 4);
  return <div className="min-h-screen flex flex-col bg-[#0a0a0a]" data-id="element-440">
      {/* Hero Section */}
      <section className="relative py-24 md:py-40 px-4 overflow-hidden" data-id="element-441">
        {/* Cinematic Lighting Background */}
        <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-442">
          {/* Projector Beam */}
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[120%] h-[150%] bg-gradient-to-b from-white/5 via-transparent to-transparent blur-3xl transform -rotate-12 opacity-30 animate-pulse" data-id="element-443"></div>

          {/* Vignette */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#0a0a0a_80%)]" data-id="element-444"></div>

          {/* Dust Motes */}
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" data-id="element-445"></div>
        </div>

        <div className="container mx-auto relative z-10 flex flex-col items-center text-center" data-id="element-446">
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-1.5 border border-[#333] bg-black/50 backdrop-blur-md rounded-full" data-id="element-447">
            <Film className="w-3 h-3 text-[#D22B2B]" data-id="element-448" />
            <span className="font-serif uppercase tracking-[0.3em] text-[10px] text-gray-400" data-id="element-449">
              Архив советского кинематографа
            </span>
          </div>

          <h1 className="font-noir text-5xl md:text-7xl lg:text-9xl font-medium text-white mb-8 leading-[0.9] tracking-tight max-w-6xl drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]" data-id="element-450">
            СОВЕТСКОЕ <br data-id="element-451" />
            <span className="text-[#D22B2B] font-light drop-shadow-[0_0_15px_rgba(210,43,43,0.5)]" data-id="element-452">
              КИНО
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl font-serif font-light leading-relaxed" data-id="element-453">
            Погрузитесь в атмосферу классического кинематографа.
            <br data-id="element-454" />
            Истории, которые живут вечно.
          </p>

          <SearchBar variant="hero" className="mb-10" data-id="element-455" />

          <div className="flex items-center gap-6" data-id="element-456">
            <Link to="/catalog" data-id="element-457">
              <Button variant="outline" className="group border-gray-600 text-gray-300 hover:border-[#D22B2B] hover:text-[#D22B2B]" data-id="element-458">
                Открыть каталог
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-459" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Conditional Content */}
      <section className="py-20 bg-[#050505] border-t border-[#222] relative" data-id="element-460">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[1px] bg-gradient-to-r from-transparent via-[#D22B2B] to-transparent opacity-50" data-id="element-461"></div>

        <div className="container mx-auto px-4" data-id="element-462">
          {isLoggedIn ? <div className="space-y-10" data-id="element-463">
              <div className="flex items-center justify-between border-b border-[#222] pb-6" data-id="element-464">
                <div className="flex items-center gap-4" data-id="element-465">
                  <div className="p-2 bg-[#D22B2B]/10 text-[#D22B2B] border border-[#D22B2B]/30 rounded-full shadow-[0_0_15px_rgba(210,43,43,0.2)]" data-id="element-466">
                    <Star className="h-5 w-5 fill-current" data-id="element-467" />
                  </div>
                  <h2 className="font-noir text-3xl md:text-4xl text-white tracking-wide" data-id="element-468">
                    Рекомендации для вас
                  </h2>
                </div>
                <Link to="/profile" className="text-xs font-serif font-bold uppercase tracking-[0.2em] text-gray-500 hover:text-[#D22B2B] transition-colors" data-id="element-469">
                  Настроить предпочтения
                </Link>
              </div>

              {loading ? <div className="flex items-center justify-center py-20" data-id="element-470">
                  <Loader2 className="w-8 h-8 text-[#D22B2B] animate-spin" data-id="element-471" />
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-472">
                  {recommendations.map((film, idx) => <FilmCard key={film.id} film={film} index={idx} data-id="element-473" />)}
                </div>}
            </div> : <div className="max-w-5xl mx-auto bg-[#111] border border-[#333] p-10 md:p-16 relative overflow-hidden shadow-2xl group" data-id="element-474">
              {/* Decorative Background */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-50" data-id="element-475"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-476">
                <div className="flex-1 text-center md:text-left" data-id="element-477">
                  <h2 className="font-noir text-3xl md:text-4xl text-white mb-6 tracking-wide" data-id="element-478">
                    Тайны киноархива
                  </h2>
                  <p className="text-gray-400 mb-8 font-serif text-lg leading-relaxed font-light" data-id="element-479">
                    Войдите в систему, чтобы открыть доступ к персональным
                    подборкам и скрытым жемчужинам коллекции.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start" data-id="element-480">
                    <Badge variant="outline" className="border-[#444] text-gray-400 px-3 py-1" data-id="element-481">
                      Мои списки
                    </Badge>
                    <Badge variant="outline" className="border-[#444] text-gray-400 px-3 py-1" data-id="element-482">
                      История просмотров
                    </Badge>
                    <Badge variant="outline" className="border-[#444] text-gray-400 px-3 py-1" data-id="element-483">
                      Редкие кадры
                    </Badge>
                  </div>
                </div>

                <div className="shrink-0" data-id="element-484">
                  <Link to="/login" data-id="element-485">
                    <Button size="lg" className="w-full md:w-auto shadow-[0_0_20px_rgba(210,43,43,0.3)]" data-id="element-486">
                      Войти в профиль
                    </Button>
                  </Link>
                </div>
              </div>
            </div>}
        </div>
      </section>
    </div>;
}